import {isFeatureEnabled} from '@github-ui/feature-flags'
import {EditIssueAssigneesSection} from '@github-ui/issue-metadata/AssigneesSection'
import {EditIssueFieldsSection} from '@github-ui/issue-metadata/FieldsSection'
import {EditIssueLabelsSection} from '@github-ui/issue-metadata/LabelsSection'
import {EditIssueMilestonesSection} from '@github-ui/issue-metadata/MilestonesSection'
import {
  EditIssueProjectsSection,
  ProjectsSectionFallback,
  type ProjectsSectionFragment,
} from '@github-ui/issue-metadata/ProjectsSection'
import {EditIssueIssueTypeSection} from '@github-ui/issue-metadata/TypesSection'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {QueryKey} from '@github-ui/react-query'
import {StickySidebar} from '@github-ui/sticky-sidebar'
import {clsx} from 'clsx'
import {Suspense, useMemo} from 'react'

import {FIXED_HEADER_HEIGHT} from './header/constants/sticky-elements'
import {issueMetadataQueryKey} from './issue-metadata-query'
import type {IssueNode} from './issue-viewer-page-query-types'
import type {IssueSecondaryNode} from './issue-viewer-secondary-query-types'
import styles from './IssueSidebar.module.css'
import {
  IssueSidebarLazySections,
  IssueSidebarLazySectionsErrorFallback,
  IssueSidebarLazySectionsFallback,
  type IssueSidebarLazySectionsFragment,
} from './IssueSidebarLazySections'
import type {IssueViewerViewerFragment} from './IssueViewer'
import type {OptionConfig} from './OptionConfig'
import {OptionsSection} from './sections/OptionsSection'

type IssueSidebarSecondaryRepository = Pick<NonNullable<IssueSecondaryNode['repository']>, 'isLocked'>
type IssueSidebarSecondarySuggestedActors = Pick<IssueSecondaryNode, 'suggestedActors'>
type IssueSidebarSecondaryFragment = Pick<IssueSecondaryNode, 'isTransferInProgress' | 'viewerCanUpdateMetadata'> &
  IssueSidebarSecondarySuggestedActors & {
    repository: IssueSidebarSecondaryRepository
  }

export type IssueSidebarSecondaryKey = IssueSidebarSecondaryFragment

type IssueSidebarProps = {
  issue: IssueSidebarPrimaryFragment
  sidebarSecondaryKey?: IssueSidebarSecondaryKey
  lazySecondaryData?: IssueSidebarLazySectionsFragment
  optionConfig: OptionConfig
  viewer: IssueViewerViewerFragment | null
  routeQueryKey?: QueryKey
}

export type IssueSidebarPrimaryFragment = Pick<
  IssueNode,
  | 'id'
  | 'number'
  | 'assignedActors'
  | 'agentAssignments'
  | 'viewerCanAssign'
  | 'labels'
  | 'viewerCanLabel'
  | 'projectItems'
  | 'milestone'
  | 'viewerCanSetMilestone'
  | 'title'
  | 'body'
  | 'isPinned'
  | 'locked'
  | 'viewerCanDelete'
  | 'viewerCanTransfer'
  | 'viewerCanConvertToDiscussion'
  | 'viewerCanLock'
  | 'issueType'
  | 'viewerCanType'
  | 'viewerCanSetFields'
  | 'issueFieldValues'
> & {
  readonly repository: Omit<IssueNode['repository'], 'owner'> & {
    readonly owner: NonNullable<IssueNode['repository']['owner']>
  }
}

export function IssueSidebar({
  issue,
  sidebarSecondaryKey: issueSecondary,
  lazySecondaryData,
  optionConfig,
  viewer,
  routeQueryKey,
}: IssueSidebarProps) {
  const modernUx = isFeatureEnabled('issues_modern_ux')

  const sidebar = useMemo(() => {
    const viewerCanPush = issue.repository.viewerCanPush
    const isRepoLocked = issueSecondary?.repository?.isLocked ?? false
    const readonly = optionConfig.isReadonly || isRepoLocked

    return (
      <div
        className={clsx(styles.sidebarContent, modernUx && styles.sidebarContentModernUx)}
        data-modern-ux={modernUx || undefined}
      >
        <EditIssueAssigneesSection
          issue={issue}
          viewer={viewer}
          lazyKey={issueSecondary ?? undefined}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <EditIssueLabelsSection
          issue={issue}
          canCreateLabel={viewerCanPush}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <EditIssueIssueTypeSection
          issue={issue}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <EditIssueFieldsSection
          issue={issue}
          onUpdate={optionConfig.onIssueUpdate}
          inProjectsPage={!!optionConfig.selectedProjectId}
          readonly={readonly}
        />
        <Suspense fallback={<ProjectsSectionFallback />}>
          <ErrorBoundary boundaryName="issue-sidebar-projects" fallback={<ProjectsSectionFallback />}>
            <EditIssueProjectsSection
              selectedProjectId={optionConfig.selectedProjectId}
              allowedProjectOwner={optionConfig.allowedProjectOwner}
              issueOrPullRequest={issue as ProjectsSectionFragment}
              viewerCanUpdateMetadata={issueSecondary?.viewerCanUpdateMetadata}
              onIssueUpdate={optionConfig.onIssueUpdate}
              onRemovedFromProject={optionConfig.onRemovedFromProject}
              insideSidePanel={optionConfig.insideSidePanel}
              readonly={readonly}
              settlementQueryKey={issueMetadataQueryKey(issue.id, optionConfig.allowedProjectOwner)}
              fieldValueQueryKeys={[
                issueMetadataQueryKey(issue.id, optionConfig.allowedProjectOwner),
                ...(routeQueryKey ? [routeQueryKey] : []),
              ]}
            />
          </ErrorBoundary>
        </Suspense>
        <EditIssueMilestonesSection
          issue={issue}
          canCreateMilestone={viewerCanPush}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <Suspense fallback={<IssueSidebarLazySectionsFallback />}>
          <ErrorBoundary boundaryName="IssueSidebarLazySections" fallback={<IssueSidebarLazySectionsErrorFallback />}>
            <IssueSidebarLazySections
              onLinkClick={optionConfig.onLinkClick}
              onIssueUpdate={optionConfig.onIssueUpdate}
              insideSidePanel={optionConfig.insideSidePanel}
              issueData={lazySecondaryData}
              viewer={viewer}
              readonly={readonly}
            />
          </ErrorBoundary>
        </Suspense>
        {!readonly && (
          <OptionsSection issue={issue} optionsSectionSecondary={issueSecondary} optionConfig={optionConfig} />
        )}
      </div>
    )
  }, [issue, issueSecondary, lazySecondaryData, optionConfig, viewer, routeQueryKey, modernUx])

  return <StickySidebar topOffset={FIXED_HEADER_HEIGHT}>{sidebar}</StickySidebar>
}
